'use client';

import { useEffect, useState } from 'react';
import { Button, CopyBtn, Modal } from '@mokoboko/components';

import { PatternModalHeader } from '@/components/dialogs/pattern-modal-header';
import { AnalyticsEvent, track } from '@/lib/analytics/tracker';
import {
  type BeforeInstallPromptEvent,
  type InstallSource,
  spendInstallPrompt,
} from '@/lib/home-screen-app-store';
import {
  APPLE_TOUCH_ICON_PATH,
  INSTALL_PATH,
  isPhonePlatform,
  type OfferPlatform,
} from '@/lib/install-platform';
import useTenant from '@/state/tenant';

// Worded for each browser's own menu labels. Safari on iOS 26 hides Share
// behind ••• in its compact layout, and keeps "Open as Web App" on by default;
// older versions have neither.
const STEPS: Record<OfferPlatform, string[]> = {
  'ios-safari': [
    'Tap the Share button, the square with an arrow. If you can’t see it, tap ••• first.',
    'Tap “Add to Home Screen”. If it’s not in the list, tap “View More”.',
    'Tap “Add”. If you see “Open as Web App”, keep it on.',
  ],
  'ios-browser': [
    'Tap the Share button. In Chrome it’s next to the address bar.',
    'Tap “Add to Home Screen”. If it’s not in the list, tap “More”.',
    'Tap “Add”.',
  ],
  'in-app': [
    'This app’s browser can’t add pages to your Home Screen.',
    'Copy the link below, or open the menu (••• or ⋮) and choose “Open in browser”.',
    'Open the page in Safari or Chrome, then tap “Add to Home Screen” again.',
  ],
  android: [
    'Open your browser’s menu, the ⋮ or ☰ button.',
    'Tap “Add to Home screen” or “Install app”.',
    'Tap “Install” or “Add”.',
  ],
  'desktop-chromium': [
    'Open your browser’s menu, the ⋮ or ⋯ button.',
    'Find “Install”. In Chrome it’s under “Cast, save, and share”, in Edge under “Apps”.',
    'Confirm, and the booking page opens in its own window.',
  ],
  'mac-safari': [
    'In the menu bar, click “File”.',
    'Click “Add to Dock”, then “Add”.',
  ],
};

// The link a copied address should carry: opened in a real browser, it shows
// these steps again.
const installLink = () => `${window.location.origin}${INSTALL_PATH}`;

const runInstallPrompt = async (
  prompt: BeforeInstallPromptEvent,
  platform: OfferPlatform,
  onClose: () => void
) => {
  spendInstallPrompt();
  await prompt.prompt();
  const { outcome } = await prompt.userChoice;
  track({
    event: AnalyticsEvent.INSTALL_PROMPT_ANSWERED,
    properties: { platform, outcome },
  });
  if (outcome === 'accepted') onClose();
};

export const InstallHelpSheet = ({
  platform,
  source,
  prompt,
  onClose,
}: {
  platform: OfferPlatform;
  source: InstallSource;
  prompt: BeforeInstallPromptEvent | null;
  onClose: () => void;
}) => {
  const { data: tenant } = useTenant();
  // As the sheet opened: the prompt is spent once used.
  const [couldPrompt] = useState(!!prompt);

  useEffect(() => {
    track({
      event: AnalyticsEvent.INSTALL_HELP_OPENED,
      properties: { platform, source, can_prompt: couldPrompt },
    });
  }, [platform, source, couldPrompt]);

  const name = tenant?.name ?? 'this studio';
  const isComputer = !isPhonePlatform(platform);

  return (
    <Modal.Root open onOpenChange={(open) => !open && onClose()}>
      <Modal.Content size="sm" hasCloseButton>
        <PatternModalHeader className="sm:px-8 sm:py-8">
          {/* The icon the phone will show, painted for this studio. */}
          <img
            src={APPLE_TOUCH_ICON_PATH}
            alt=""
            width={64}
            height={64}
            className="relative size-16 rounded-[14px] shadow-md"
          />
          <Modal.Title className="relative mt-5 text-xl text-gray-900">
            {isComputer ? `Install ${name}` : `Add ${name} to your Home Screen`}
          </Modal.Title>
          <Modal.Description className="relative mt-1 max-w-sm text-gray-700">
            {isComputer
              ? 'Open the booking page in its own window, like an app.'
              : 'Open it like an app, right from your Home Screen.'}
          </Modal.Description>
        </PatternModalHeader>

        <Modal.Body className="flex flex-col gap-6 p-5 pb-8 pt-0 sm:px-8 sm:pb-8">
          {prompt ? (
            <Button
              variant="primary"
              size="lg"
              className="w-full justify-center"
              onClick={() => runInstallPrompt(prompt, platform, onClose)}
            >
              Install
            </Button>
          ) : (
            <ol className="flex flex-col gap-3">
              {STEPS[platform].map((step, index) => (
                <li
                  key={step}
                  className="flex gap-3 text-sm leading-5 text-gray-700"
                >
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-900">
                    {index + 1}
                  </span>
                  <span className="pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
          )}

          {platform === 'in-app' && (
            <CopyBtn
              text={installLink()}
              variant="secondary-gray"
              size="lg"
              showLabel
              idleLabel="Copy link"
              copiedLabel="Link copied"
              className="w-full justify-center"
            />
          )}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
